import { createContext, useContext, useState, useMemo } from 'react'
import PropTypes from 'prop-types'

// Define default context values to avoid null checks when using the context
const defaultContextValue = {
  paymentIntentId: null,
  setPaymentIntentId: () => {},
  disabled: false,
  setDisabled: () => {},
}

// Create context with default values
const QuickCheckoutEmrOrdersContext = createContext(defaultContextValue)

/**
 * Custom hook to access QuickCheckoutEmrOrders context
 *
 * @returns {object} The context value
 */
export const useQuickCheckoutOrders = () => {
  const context = useContext(QuickCheckoutEmrOrdersContext)
  if (context === undefined) {
    throw new Error('useQuickCheckoutOrders must be used within a QuickCheckoutEmrOrdersProvider')
  }
  return context
}

/** Provider component for QuickCheckoutEmrOrders context */
const QuickCheckoutEmrOrdersProvider = ({ paymentIntent, children }) => {
  const [paymentIntentId, setPaymentIntentId] = useState(paymentIntent)
  const [disabled, setDisabled] = useState(false)

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      paymentIntentId,
      setPaymentIntentId,
      disabled,
      setDisabled,
    }),
    [paymentIntentId, disabled],
  )

  return (
    <QuickCheckoutEmrOrdersContext.Provider value={value}>
      {children}
    </QuickCheckoutEmrOrdersContext.Provider>
  )
}

QuickCheckoutEmrOrdersProvider.propTypes = {
  paymentIntent: PropTypes.string,
  children: PropTypes.node.isRequired,
}

QuickCheckoutEmrOrdersProvider.defaultProps = {
  paymentIntent: null,
}

export default QuickCheckoutEmrOrdersProvider
